import { cn } from "@/v2/lib/utils";

export interface PresetChip {
	value: string;
	label: string;
}

export interface PresetChipsProps {
	chips: PresetChip[];
	activeValue: string | null;
	/**
	 * Called when the user clicks a chip. Receives the chip's value, or `null`
	 * when the user clicked an already-active chip (i.e., deselect — host
	 * should clear `activeValue` but leave the form input alone).
	 */
	onPick: (value: string | null) => void;
	className?: string;
}

/**
 * Horizontally-scrollable row of chip buttons. The host owns form state; chips
 * are pure UX sugar — clicking one calls `onPick(value)`. Clicking an
 * already-active chip calls `onPick(null)` (deselect, per `wire.md`).
 *
 * Renders nothing when `chips` is empty.
 */
export function PresetChips({ chips, activeValue, onPick, className }: PresetChipsProps) {
	if (chips.length === 0) return null;
	return (
		<div className={cn("flex gap-1.5 overflow-x-auto -mx-0.5 px-0.5 py-0.5 scrollbar-thin", className)}>
			{chips.map((chip) => {
				const active = chip.value === activeValue;
				return (
					<button
						key={chip.value}
						type="button"
						onClick={() => onPick(active ? null : chip.value)}
						className={cn(
							"shrink-0 rounded-full border px-3 py-1 text-xs font-medium transition-colors",
							active
								? "border-primary bg-primary text-primary-foreground"
								: "border-input bg-card text-foreground hover:bg-muted",
						)}
					>
						{chip.label}
					</button>
				);
			})}
		</div>
	);
}
